/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-browser";
import { msalInstance, loginRequest } from "@/lib/msalInstance";
import { logger, LogCategory } from "@/lib/logger";
import type { User, Role, Permission } from "@/lib/types";

// ============================================================================
// Types
// ============================================================================

interface MsalLoginOptions {
  usePopup?: boolean;
  scopes?: string[];
}

interface AuthState {
  // State
  user: User | null;
  roles: Role[];
  permissions: Permission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Demo mode (for development without MSAL)
  isDemoMode: boolean;

  // Actions - MSAL integrated
  loginWithMsal: (options?: MsalLoginOptions) => Promise<void>;
  logoutFromMsal: () => Promise<void>;
  acquireAccessToken: (scopes?: string[]) => Promise<string | null>;
  refreshUserFromMsal: () => Promise<void>;

  // Legacy actions (kept for backward compatibility)
  setUser: (user: User | null) => void;
  setRoles: (roles: Role[]) => void;
  setPermissions: (permissions: Permission[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, roles: Role[], permissions: Permission[]) => void;
  logout: () => void;

  // Demo mode actions
  enableDemoMode: (user: User) => void;
  disableDemoMode: () => void;

  // Permission checks
  hasPermission: (permissionCode: string) => boolean;
  hasRole: (roleName: string) => boolean;
}

// ============================================================================
// Demo User (for development)
// ============================================================================

export const DEMO_USER: User = {
  id: "demo-user-001",
  email: "demo@torqe.cms.gov",
  fullName: "Demo User",
  firstName: "Demo",
  lastName: "User",
  displayName: "User, Demo (CMS)",
  department: "CMCS",
  jobTitle: "Data Quality Analyst",
  primaryRoleId: "role-system_admin",
  primaryRole: {
    id: "role-system_admin",
    name: "system_admin",
    displayName: "System Administrator",
    isSuperUser: true,
    isSystemRole: true,
    isActive: true,
  },
  isActive: true,
  createdAt: new Date().toISOString(),
};

// Demo permissions for VITE_DEMO_MODE local dev. Aligned with auth.permissions
// in the live DB after Phase A policy migration (20 permissions total). If
// you add/remove permissions in a migration, update this list too.
const DEMO_PERMISSIONS: Permission[] = [
  { code: "dashboard:view", name: "View Dashboard & KPIs", category: "dashboard" },
  { code: "submissions:view", name: "View Submissions", category: "submissions" },
  { code: "submissions:create", name: "Create Submission", category: "submissions" },
  {
    code: "submissions:manage",
    name: "Edit / Delete Submissions & Files",
    category: "submissions",
  },
  { code: "submissions:view_drafts", name: "View Other Users' Drafts", category: "submissions" },
  { code: "dq:view", name: "View DQ Results & Insights", category: "dq" },
  { code: "incidents:view", name: "View Incidents", category: "incidents" },
  { code: "incidents:manage", name: "Create / Edit / Resolve Incidents", category: "incidents" },
  { code: "analytics:view", name: "View Analytics & Trends", category: "analytics" },
  { code: "analytics:export", name: "Export Analytics", category: "analytics" },
  { code: "admin:view_reference", name: "View Admin Reference Pages", category: "admin" },
  { code: "admin:edit_config", name: "Edit Config & Thresholds", category: "admin" },
  { code: "admin:manage_users", name: "Manage Users / Roles / IdP", category: "admin" },
  {
    code: "admin:manage_integrations",
    name: "Manage Integrations & Notifications",
    category: "admin",
  },
  { code: "admin:view_audit", name: "View Audit Log", category: "admin" },
  { code: "change_requests:view", name: "View Change Requests", category: "change_requests" },
  { code: "change_requests:submit", name: "Submit Change Request", category: "change_requests" },
  {
    code: "change_requests:approve",
    name: "Approve / Reject Change Requests",
    category: "change_requests",
  },
  { code: "profile:view", name: "View Own Profile", category: "profile" },
  { code: "profile:edit", name: "Edit Own Profile", category: "profile" },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Maps MSAL account info to TORQE User type
 */
function mapMsalAccountToUser(account: AccountInfo): User {
  const idTokenClaims = account.idTokenClaims;

  // Extract name parts
  const fullName = idTokenClaims?.name ?? account.name ?? "Unknown User";
  const nameParts = fullName.split(" ");
  /* v8 ignore next -- unreachable: String#split with a non-empty separator always returns an array of length >= 1, so nameParts[0] can never be undefined */
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // Extract additional claims from Azure AD
  const jobTitle = idTokenClaims?.jobTitle as string | undefined;
  const department = idTokenClaims?.department as string | undefined;
  const roles = idTokenClaims?.roles ?? [];

  // Map Azure AD role to TORQE role (null if no role found in token claims)
  const primaryRoleName = roles.includes("Admin")
    ? "system_admin"
    : roles.includes("StateUser")
      ? "state_admin"
      : null;

  const isSuperUser = roles.includes("Admin");

  return {
    id: account.localAccountId,
    email: account.username,
    fullName,
    firstName,
    lastName,
    displayName: fullName,
    department,
    jobTitle,
    primaryRoleId: primaryRoleName ? `role-${primaryRoleName}` : "",
    primaryRole: primaryRoleName
      ? {
          id: `role-${primaryRoleName}`,
          name: primaryRoleName,
          displayName:
            primaryRoleName === "system_admin" ? "System Administrator" : "State Administrator",
          isSuperUser,
          isSystemRole: true,
          isActive: true,
        }
      : undefined,
    isActive: true,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Checks if demo mode is enabled via environment variable
 */
function isDemoModeEnabled(): boolean {
  return import.meta.env.VITE_DEMO_MODE === "true";
}

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      roles: [],
      permissions: [],
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isDemoMode: false,

      // ======================================================================
      // MSAL Actions
      // ======================================================================

      /**
       * Login with MSAL (Azure AD)
       */
      loginWithMsal: async (options?: MsalLoginOptions) => {
        const { usePopup = true, scopes = loginRequest.scopes } = options || {};

        logger.debug(LogCategory.AUTH, "Starting MSAL login", {
          usePopup,
          scopes,
        });

        set({ isLoading: true, error: null });

        try {
          let response: AuthenticationResult;

          if (usePopup) {
            // Popup login (recommended for better UX)
            logger.debug(LogCategory.AUTH, "Attempting popup login");
            response = await msalInstance.loginPopup({ scopes });
            logger.debug(LogCategory.AUTH, "Popup login successful", {
              hasAccount: !!response.account,
              scopes: response.scopes,
            });
          } else {
            // Redirect login (fallback for popup blockers)
            logger.debug(LogCategory.AUTH, "Attempting redirect login");
            await msalInstance.loginRedirect({ scopes });
            // loginRedirect never returns - page will reload
            return;
          }

          // Set the active account
          msalInstance.setActiveAccount(response.account);
          logger.debug(LogCategory.AUTH, "Active account set");

          // Map MSAL account to TORQE user
          const user = mapMsalAccountToUser(response.account);
          logger.debug(LogCategory.AUTH, "User mapped from MSAL account", {
            primaryRole: user.primaryRole?.name,
          });

          // Extract roles from ID token
          const idTokenClaims = response.account.idTokenClaims;
          const azureRoles = idTokenClaims?.roles ?? [];
          logger.debug(LogCategory.AUTH, "Azure AD roles extracted", {
            roleCount: azureRoles.length,
          });

          // Map to TORQE roles
          const roles = user.primaryRole ? [user.primaryRole] : [];

          // Permissions loaded from /auth/me API via useCurrentUser, not from store
          const permissions: Permission[] = [];

          set({
            user,
            roles,
            permissions,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isDemoMode: false,
          });

          logger.info(LogCategory.AUTH, "Login complete - user authenticated");
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to authenticate with Azure AD";

          set({
            user: null,
            roles: [],
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
            isDemoMode: false,
          });

          throw error;
        }
      },

      /**
       * Logout from MSAL
       */
      logoutFromMsal: async () => {
        set({ isLoading: true });

        try {
          const account = msalInstance.getActiveAccount();

          if (account) {
            // Tell the backend to record a logout audit row BEFORE MSAL
            // redirects (after redirect the bearer token is gone and the
            // backend can't identify the caller). Non-fatal: if the
            // audit write fails we still log the user out.
            try {
              const { authenticatedApi } = await import("@/api/authenticatedClient");
              await authenticatedApi.post("/auth/logout");
            } catch (auditError) {
              logger.warn(
                LogCategory.AUTH,
                "Logout audit write failed; proceeding with MSAL logout",
                { error: auditError instanceof Error ? auditError.message : String(auditError) }
              );
            }

            // Use redirect logout to avoid COOP issues
            await msalInstance.logoutRedirect({
              account,
              postLogoutRedirectUri: window.location.origin,
            });
            // Note: logoutRedirect never returns - page will reload
          } else {
            // No account, just clear local state
            set({
              user: null,
              roles: [],
              permissions: [],
              isAuthenticated: false,
              isLoading: false,
              error: null,
              isDemoMode: false,
            });
          }
        } catch (error) {
          logger.error(LogCategory.AUTH, "MSAL logout error", {
            error: error instanceof Error ? error.message : String(error),
          });

          // Clear state even if logout fails
          set({
            user: null,
            roles: [],
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
            error: "Logout encountered an issue but local session was cleared",
            isDemoMode: false,
          });
        }
      },

      /**
       * Acquire access token for API calls
       */
      acquireAccessToken: async (scopes?: string[]) => {
        try {
          const account = msalInstance.getActiveAccount();

          if (!account) {
            logger.warn(LogCategory.AUTH, "No active MSAL account found");
            return null;
          }

          // Try silent token acquisition first
          try {
            const response = await msalInstance.acquireTokenSilent({
              scopes: scopes || loginRequest.scopes,
              account,
            });

            return response.accessToken;
          } catch (silentError) {
            // Handle block_iframe_reload error - browser blocked iframe-based token refresh
            const isBlockIframeError =
              silentError instanceof BrowserAuthError &&
              (silentError.errorCode === "block_iframe_reload" ||
                silentError.errorCode === "block_iframe");

            if (isBlockIframeError) {
              logger.warn(
                LogCategory.AUTH,
                "Browser blocked iframe-based token refresh, trying popup"
              );

              try {
                // Try popup-based token acquisition (doesn't use iframe)
                const popupResponse = await msalInstance.acquireTokenPopup({
                  scopes: scopes || loginRequest.scopes,
                  account,
                });
                return popupResponse.accessToken;
              } catch {
                logger.warn(
                  LogCategory.AUTH,
                  "Popup token acquisition also failed, using redirect"
                );
                // Fall back to redirect
                await msalInstance.acquireTokenRedirect({
                  scopes: scopes || loginRequest.scopes,
                  account,
                });
                // Note: acquireTokenRedirect never returns - page will reload
                return null;
              }
            }

            // If silent fails with InteractionRequiredAuthError, use redirect
            if (silentError instanceof InteractionRequiredAuthError) {
              logger.info(
                LogCategory.AUTH,
                "Silent token acquisition failed, redirecting to login"
              );

              // Use redirect instead of popup to avoid COOP issues
              await msalInstance.acquireTokenRedirect({
                scopes: scopes || loginRequest.scopes,
                account,
              });
              // Note: acquireTokenRedirect never returns - page will reload
              return null;
            }

            throw silentError;
          }
        } catch (error) {
          logger.error(LogCategory.AUTH, "Token acquisition error", {
            error: error instanceof Error ? error.message : String(error),
          });

          const errorMessage =
            error instanceof Error ? error.message : "Failed to acquire access token";

          set({ error: errorMessage });

          return null;
        }
      },

      /**
       * Refresh user information from MSAL
       */
      refreshUserFromMsal: () => {
        try {
          const account = msalInstance.getActiveAccount();

          if (!account) {
            logger.warn(LogCategory.AUTH, "No active MSAL account to refresh from");
            return Promise.resolve();
          }

          // Map account to user
          const user = mapMsalAccountToUser(account);

          // Update state - permissions loaded from /auth/me API via useCurrentUser, not from store
          const roles = user.primaryRole ? [user.primaryRole] : [];
          const permissions: Permission[] = [];

          set({
            user,
            roles,
            permissions,
            isAuthenticated: true,
          });
        } catch (error) {
          logger.error(LogCategory.AUTH, "Failed to refresh user from MSAL", {
            error: error instanceof Error ? error.message : String(error),
          });
        }
        return Promise.resolve();
      },

      // ======================================================================
      // Legacy Actions (kept for backward compatibility)
      // ======================================================================

      setUser: (user) =>
        set({
          user,
          isAuthenticated: user !== null,
        }),

      setRoles: (roles) => set({ roles }),

      setPermissions: (permissions) => set({ permissions }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      login: (user, roles, permissions) =>
        set({
          user,
          roles,
          permissions,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          isDemoMode: false,
        }),

      logout: () =>
        set({
          user: null,
          roles: [],
          permissions: [],
          isAuthenticated: false,
          isLoading: false,
          error: null,
          isDemoMode: false,
        }),

      // ======================================================================
      // Demo Mode Actions
      // ======================================================================

      enableDemoMode: (user) => {
        if (!isDemoModeEnabled()) {
          logger.warn(LogCategory.AUTH, "Demo mode is disabled via environment variable");
          return;
        }

        set({
          user,
          roles: user.primaryRole ? [user.primaryRole] : [],
          permissions: DEMO_PERMISSIONS,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          isDemoMode: true,
        });
      },

      disableDemoMode: () =>
        set({
          user: null,
          roles: [],
          permissions: [],
          isAuthenticated: false,
          isDemoMode: false,
        }),

      // ======================================================================
      // Permission Checks
      // ======================================================================

      hasPermission: (permissionCode) => {
        const { user, permissions } = get();

        // Super users have all permissions
        if (user?.primaryRole?.isSuperUser) {
          return true;
        }

        return permissions.some((p) => p.code === permissionCode);
      },

      hasRole: (roleName) => {
        const { roles } = get();
        return roles.some((r) => r.name === roleName);
      },
    }),
    {
      name: "torqe-auth",
      storage: createJSONStorage(() => sessionStorage), // SECURITY: Use sessionStorage, NOT localStorage
      partialize: (state) => ({
        // SECURITY: Only persist non-PII auth state. User object is reconstructed from MSAL on rehydrate.
        isAuthenticated: state.isAuthenticated,
        isDemoMode: state.isDemoMode,
      }),
      onRehydrateStorage: () => (state) => {
        // After rehydration, reconstruct user from MSAL account (no PII in sessionStorage)
        const msalAccount = msalInstance.getActiveAccount();

        if (state && state.isAuthenticated && !msalAccount && !state.isDemoMode) {
          // MSAL logged out but session thinks authenticated - clear state
          logger.debug(
            LogCategory.AUTH,
            "MSAL logged out but session was authenticated - clearing state"
          );
          state.isAuthenticated = false;
          state.user = null;
          state.roles = [];
          state.permissions = [];
        } else if (state && msalAccount) {
          // Reconstruct user from MSAL account
          const user = mapMsalAccountToUser(msalAccount);
          state.user = user;
          state.isAuthenticated = true;
          state.roles = user.primaryRole ? [user.primaryRole] : [];
          state.permissions = []; // Permissions loaded from /auth/me API via useCurrentUser
        }
      },
    }
  )
);

// ============================================================================
// Fine-grained Selectors (MUST use for optimal re-renders)
// ============================================================================

export const useUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useIsLoading = () => useAuthStore((s) => s.isLoading);
export const useAuthError = () => useAuthStore((s) => s.error);
export const useIsDemoMode = () => useAuthStore((s) => s.isDemoMode);
/** @deprecated Use usePermissions() from context/PermissionContext instead for RBAC checks */
export const useStorePermissions = () => useAuthStore((s) => s.permissions);
export const useRoles = () => useAuthStore((s) => s.roles);

// Permission check hook
export const useHasPermission = (permissionCode: string) =>
  useAuthStore((s) => {
    if (s.user?.primaryRole?.isSuperUser) return true;
    return s.permissions.some((p) => p.code === permissionCode);
  });

// Role check hook
export const useHasRole = (roleName: string) =>
  useAuthStore((s) => s.roles.some((r) => r.name === roleName));

// User display name
export const useUserDisplayName = () =>
  useAuthStore((s) => s.user?.displayName || s.user?.fullName || "Guest");

// User initials
export const useUserInitials = () =>
  useAuthStore((s) => {
    const name = s.user?.fullName || "";
    return name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  });
